/**
 * Server-side tracking for long-running work a user started and then walked
 * away from.
 *
 * The browser already has a background-jobs panel, but it only knows about work
 * this tab started: close the tab and the job is gone, with no way to tell a
 * finished upload from an abandoned one. These rows are the durable counterpart
 * — the thing a reload, a new tab, or a notification can read.
 *
 * Execution lives in Temporal; a row here is the projection of that run which
 * the UI can actually query. `wfv2_workflow_runs` plays the same role for the
 * workflow engine, but it is bound to a user-defined `WorkflowDefinition`, so it
 * cannot carry internal work like this.
 */

/**
 * Where a job has got to.
 *
 * `Queued` means accepted but not yet picked up, which is a real state here: the
 * row is written in the same transaction that persists the upload, before the
 * workflow that does the work has started.
 */
export enum BackgroundJobStatus {
  Queued = "queued",
  Running = "running",
  Succeeded = "succeeded",
  Failed = "failed",
}

/** True once the job has reached a state it will not leave on its own. */
export function isBackgroundJobSettled(status: BackgroundJobStatus): boolean {
  return (
    status === BackgroundJobStatus.Succeeded ||
    status === BackgroundJobStatus.Failed
  );
}

/**
 * What kind of work a job represents.
 *
 * Stored as a plain string rather than a Postgres enum, matching
 * `comm_notification_events.event_type`: a new kind of background work should
 * not need a migration, and the consumers that care about a specific kind
 * already have to handle unrecognized ones (an older tab polling after a deploy
 * has shipped a new kind).
 */
export enum BackgroundJobKind {
  PricelistExtraction = "pricelist_extraction",
  /**
   * A Ship & Debit 844 "Batch and Send". Large divisions (W/+ spans ~100 DUNS
   * batches) take several minutes — past the browser's ~300s fetch limit — so
   * the send runs in Temporal and the staged page polls the job. The full send
   * result (successful/failed/submissions) is stored on the job's payload under
   * `result` when it succeeds.
   */
  Sd844BatchSend = "sd_844_batch_send",
  /**
   * A CSV → XLSX conversion through the Item Master utility. The upload is
   * staged to blob storage by the browser; a Temporal workflow streams it
   * through the converter and stores the workbook as a document. On success the
   * job's `entityId` is the XLSX document and the payload carries
   * `{ fileName, xlsxDocumentId, rowCount }`.
   */
  CsvToXlsxConversion = "csv_to_xlsx_conversion",
}
